// frontend/src/pages/EventsPage.js
import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import apiService from "../services/apiService";
import LoadingSpinner from "../components/common/LoadingSpinner";
import EventCard from "../components/event/EventCard";
import { getEventLiveStatus } from "../utils/eventTimeUtils";
import { Calendar, History, User, PlayCircle } from "lucide-react";

const EventsPage = () => {
  const { isAuthenticated } = useAuth();
  const { language, t } = useLanguage();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState(searchParams.get("tab") || "upcoming");
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const LIMIT = 12;

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    if (newFilter === "upcoming") {
      setSearchParams({});
    } else {
      setSearchParams({ tab: newFilter });
    }
  };

  useEffect(() => {
    // Reset and fetch when filter or language changes
    setEvents([]);
    setOffset(0);
    setHasMore(true);
    fetchEvents(0, true);

    window.scrollTo(0, 0);
  }, [filter, language]);

  useEffect(() => {
    // Infinite scroll handler
    const handleScroll = () => {
      if (loadingMore || !hasMore) return;

      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;

      if (scrollTop + clientHeight >= scrollHeight * 0.8) {
        loadMoreEvents();
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [loadingMore, hasMore]);

  const fetchEvents = async (newOffset = 0, isInitial = false) => {
    try {
      if (isInitial) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      setError(null);

      let response;
      if (filter === "my" && isAuthenticated) {
        response = await apiService.getMyEvents(language, "all");
      } else {
        response = await apiService.getEvents(language, {
          status: filter,
          limit: LIMIT,
          offset: newOffset,
        });
      }

      if (response.success) {
        let newEvents = response.data || [];

        // Sort live/starting-soon events to top for upcoming filter
        if (filter === "upcoming" && isInitial) {
          newEvents = [...newEvents].sort((a, b) => {
            const aStatus = getEventLiveStatus(a);
            const bStatus = getEventLiveStatus(b);
            if (aStatus.isLive && !bStatus.isLive) return -1;
            if (!aStatus.isLive && bStatus.isLive) return 1;
            if (aStatus.isStartingSoon && !bStatus.isStartingSoon) return -1;
            if (!aStatus.isStartingSoon && bStatus.isStartingSoon) return 1;
            return new Date(a.start_datetime) - new Date(b.start_datetime);
          });
        }

        if (isInitial) {
          setEvents(newEvents);
          setOffset(newEvents.length);
        } else {
          setEvents((prev) => {
            const existingIds = new Set(prev.map((e) => e.id));
            const uniqueNew = newEvents.filter((e) => !existingIds.has(e.id));
            return [...prev, ...uniqueNew];
          });
          setOffset((prevOffset) => prevOffset + newEvents.length);
        }

        // For "my" filter, no pagination
        if (filter === "my") {
          setHasMore(false);
        } else {
          setHasMore(response.pagination?.hasMore || false);
        }
      } else {
        setError("Failed to load events");
      }
    } catch (err) {
      console.error("Error fetching events:", err);
      setError("Failed to load events. Please try again later.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreEvents = () => {
    if (!loadingMore && hasMore && filter !== "my") {
      fetchEvents(offset, false);
    }
  };

  // Filter tabs
  const filterTabs = [
    {
      id: "upcoming",
      label: { de: "Kommende", en: "Upcoming" },
      icon: Calendar,
    },
    {
      id: "recordings",
      label: { de: "Aufzeichnungen", en: "Recordings" },
      icon: PlayCircle,
    },
    {
      id: "past",
      label: { de: "Vergangene", en: "Past" },
      icon: History,
    },
  ];

  // Add "My Events" tab if authenticated
  if (isAuthenticated) {
    filterTabs.push({
      id: "my",
      label: { de: "Meine Events", en: "My Events" },
      icon: User,
    });
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => fetchEvents(0, true)}
            className="bg-[#00A3E0] text-white px-4 py-2 rounded-md hover:bg-[#002855]"
          >
            {t({ de: "Erneut versuchen", en: "Try Again" })}
          </button>
        </div>
      </div>
    );
  }

  // Split featured event from rest
  const featuredEvent =
    filter === "upcoming" && events.length > 0 && events[0].featured
      ? events[0]
      : null;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[#002855] mb-2">
            {t({ de: "Veranstaltungen", en: "Events" })}
          </h1>
          <p className="text-gray-600">
            {t({
              de: "Webinare, Vorträge, Workshops und Kurse der burnair Academy",
              en: "Webinars, talks, workshops and courses from burnair Academy",
            })}
          </p>
        </div>

        {/* Filter Tabs */}
        <div className="flex mb-8 border-b border-gray-200 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
          {filterTabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => handleFilterChange(tab.id)}
                className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-3 font-medium transition-colors whitespace-nowrap text-sm sm:text-base shrink-0 ${
                  filter === tab.id
                    ? "text-[#EE005F] border-b-2 border-[#EE005F]"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                {t(tab.label)}
              </button>
            );
          })}
        </div>

        {/* Empty State */}
        {events.length === 0 && (
          <div className="text-center py-16">
            <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">
              {filter === "upcoming"
                ? t({ de: "Keine kommenden Events", en: "No upcoming events" })
                : filter === "past"
                ? t({ de: "Keine vergangenen Events", en: "No past events" })
                : t({ de: "Keine Events gefunden", en: "No events found" })}
            </h3>
            <p className="text-gray-500">
              {filter === "my"
                ? t({
                    de: "Sie sind noch für keine Events angemeldet",
                    en: "You haven't registered for any events yet",
                  })
                : t({
                    de: "Schauen Sie bald wieder vorbei",
                    en: "Check back soon",
                  })}
            </p>
          </div>
        )}

        {/* Event Grid */}
        {events.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map((event) => (
              <EventCard
                key={event.id}
                event={event}
                featured={event === featuredEvent}
              />
            ))}
          </div>
        )}

        {/* Loading More Indicator */}
        {loadingMore && (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        )}
      </div>
    </div>
  );
};

export default EventsPage;
